import { SUPABASE_URL } from './constants';
import { supabase } from './supabase';

export interface HouseholdInvitePayload {
  homeId: string;
  email: string;
  role: 'co_owner' | 'member';
  expiresInDays?: number;
}

const getAuthToken = async (): Promise<string> => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Missing authenticated session');
  return token;
};

export const createHomeInvitation = async (payload: HouseholdInvitePayload) => {
  const token = await getAuthToken();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/create-home-invitation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create invitation');
  return data;
};

export const acceptHomeInvitation = async (inviteToken: string) => {
  const token = await getAuthToken();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/accept-home-invitation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ token: inviteToken }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to accept invitation');
  return data;
};

export const revokeHomeInvitation = async (invitationId: string) => {
  const token = await getAuthToken();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/revoke-home-invitation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ invitationId }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to revoke invitation');
  return data;
};

export const removeHomeMember = async (homeId: string, userId: string) => {
  const token = await getAuthToken();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/remove-home-member`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ homeId, userId }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to remove household member');
  return data;
};

export const transferHomeOwnership = async (homeId: string, newOwnerUserId: string) => {
  const token = await getAuthToken();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/transfer-home-ownership`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ homeId, newOwnerUserId }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to transfer ownership');
  return data;
};
